import React, { useState, useRef } from 'react';
import { useToast } from '../../context/ToastContext';
import { barberService } from '../../services/barberService';

interface ImageUploadProps {
    currentImageUrl?: string;
    onImageUploaded: (imageUrl: string) => void;
    label?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    currentImageUrl,
    onImageUploaded,
    label = "Foto de Perfil"
}) => {
    const { showToast } = useToast();
    const [uploading, setUploading] = useState(false);

    // Helper to build full URL from relative path
    const buildFullUrl = (url?: string): string | undefined => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        // Use /api prefix to route through the proxy (Traefik/Nginx)
        return `/api${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const [previewUrl, setPreviewUrl] = useState<string | undefined>(buildFullUrl(currentImageUrl));
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToast('Por favor selecciona un archivo de imagen válido', 'error');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showToast('La imagen no debe superar los 5MB', 'error');
            return;
        }

        try {
            setUploading(true);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);

            // Upload to server
            const response = await barberService.uploadProfileImage(file);

            // Keep the data:URL preview from FileReader (already set above)
            // Only pass the relative path to the parent for database storage
            onImageUploaded(response.url);
            showToast('Imagen subida correctamente', 'success');
        } catch (error: any) {
            console.error('Error uploading image:', error);
            showToast(error.response?.data?.message || 'Error al subir la imagen', 'error');
            setPreviewUrl(currentImageUrl);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = () => {
        setPreviewUrl(undefined);
        onImageUploaded('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#a3a3a3'
            }}>
                📷 {label}
            </label>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                backgroundColor: '#1a1a1a',
                borderRadius: '0.75rem',
                border: '1px solid #333'
            }}>
                {/* Preview */}
                <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    backgroundColor: previewUrl ? 'transparent' : '#2a2a2a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '2px solid #404040',
                    flexShrink: 0
                }}>
                    {previewUrl ? (
                        <img
                            src={previewUrl}
                            alt="Preview"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <span style={{ fontSize: '2.5rem', color: '#737373' }}>👤</span>
                    )}
                </div>

                {/* Upload controls */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={uploading}
                    />

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={handleButtonClick}
                            disabled={uploading}
                            className="btn btn-sm"
                            style={{ fontSize: '0.875rem', background: 'transparent', border: '1px solid #f59e0b', color: '#f59e0b', borderRadius: '0.5rem' }}
                        >
                            {uploading ? '⏳ Subiendo...' : '📁 Seleccionar Imagen'}
                        </button>

                        {previewUrl && (
                            <button
                                type="button"
                                onClick={handleRemoveImage}
                                disabled={uploading}
                                className="btn btn-sm"
                                style={{ fontSize: '0.875rem', background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '0.5rem' }}
                            >
                                🗑️ Quitar
                            </button>
                        )}
                    </div>

                    <p style={{
                        margin: 0,
                        fontSize: '0.75rem',
                        color: '#737373'
                    }}>
                        Formatos: JPG, PNG, GIF. Máximo 5MB
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ImageUpload;
